import Tabs from "@/components/Tabs";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
  DOMAIN,
  PERMISSION,
  useCanManageJobPostsBool,
  useCanViewCareerPlanBool,
  useCanViewCareerPreferencesBool,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { canAccessCareerOverview } from "./_components/careerOverviewUtils";

export default function TabLayout({ children }: { children: React.ReactNode }) {
  const { pgaId } = useParams();

  const location = useLocation();
  const { pathname } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();
  const flags = useFlags();
  const careerAwardsEnabled = flags["backoffice-career-awards"] === true;
  const careerTargetsEnabled = flags["pga-career-targets"] === true;
  const canViewCareerPlan = useCanViewCareerPlanBool();
  const canViewCompensationProfiles = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewCompensationProfiles
  });
  const canViewCareerPreferences = useCanViewCareerPreferencesBool();
  const canManageJobPosts = useCanManageJobPostsBool();
  const canViewJobPostings = useHasCapabilityBool({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.viewJobPosts
  });
  const showOverview = canAccessCareerOverview({
    canViewCareerPreferences,
    canViewCompensationProfiles,
    canManageJobPosts
  });

  if (!pgaId) return null;

  const allTabItems = [
    {
      label: "Overview",
      to: "/people/:pgaId/career/overview" as Path,
      active: currentUrl === `/people/${pgaId}/career/overview`
    },
    {
      label: "Employment",
      to: "/people/:pgaId/career/employment" as Path,
      active: currentUrl === `/people/${pgaId}/career/employment`
    },
    ...(careerAwardsEnabled
      ? [
          {
            label: "Awards",
            to: "/people/:pgaId/career/awards" as Path,
            active: currentUrl === `/people/${pgaId}/career/awards`
          }
        ]
      : []),
    {
      label: "Governance Roles",
      to: "/people/:pgaId/career/governance-roles" as Path,
      active: currentUrl === `/people/${pgaId}/career/governance-roles`
    },
    {
      label: "Career Profile",
      to: "/people/:pgaId/career/career-profile" as Path,
      active:
        currentUrl.startsWith(`/people/${pgaId}/career/career-profile`) ||
        currentUrl.startsWith(`/people/${pgaId}/career/career-plan`)
    },
    {
      label: "Applications",
      to: "/people/:pgaId/career/job-applications" as Path,
      active: currentUrl === `/people/${pgaId}/career/job-applications`
    },
    {
      label: "Preferences",
      to: "/people/:pgaId/career/job-preferences" as Path,
      active: currentUrl === `/people/${pgaId}/career/job-preferences`
    },
    {
      label: "Compensation",
      to: "/people/:pgaId/career/compensation" as Path,
      active: currentUrl === `/people/${pgaId}/career/compensation`
    },
    {
      label: "Job Postings",
      to: "/people/:pgaId/career/job-postings" as Path,
      active: currentUrl === `/people/${pgaId}/career/job-postings`
    }
  ];

  const visibleTabItems = allTabItems.filter((tab) => {
    if (tab.label === "Overview") {
      return showOverview;
    }
    if (tab.label === "Employment") {
      return true;
    }
    if (tab.label === "Awards") {
      return careerAwardsEnabled;
    }
    if (tab.label === "Career Profile") {
      return careerTargetsEnabled && canViewCareerPlan;
    }
    if (tab.label === "Governance Roles") {
      return true;
    }
    if (tab.label === "Applications") {
      return canManageJobPosts;
    }
    if (tab.label === "Preferences") {
      return canViewCareerPreferences;
    }
    if (tab.label === "Compensation") {
      return canViewCompensationProfiles;
    }
    if (tab.label === "Job Postings") {
      return canViewJobPostings;
    }
    return false;
  });

  return (
    <>
      <Tabs
        tabItems={visibleTabItems.map(({ label, to, active }) => ({
          label,
          onClick: () => navigate(to, { params: { pgaId } }),
          active
        }))}
      />
      <div className="flex flex-col" data-testid="tab-content">
        {children}
      </div>
    </>
  );
}
